import { DocumentTemplateSelect } from "@/components/DocumentTemplateSelect";
// Full-page quote workspace — /app/quotes/new and /app/quotes/:qid
// Premium estimating experience: sections, line items, discounts, GST,
// autosave, attachments, activity log. NOT a modal.
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, ChevronDown, Check, FileText, Layers, Library, Loader2, Plus, Printer, Send,
  Eye, Hammer, Package, Truck, Briefcase, Receipt, CalendarClock, CheckCircle2, XCircle, Maximize2, Minimize2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  CustomerSection, QuoteDetailsSection, LineItemsTable, LinkedJobSection, AttachmentsSection,
  NotesSection, TermsSection, ActivitySection, SummaryPanel, DiscountPanel, TaxPanel,
  SettingsPanel, QuickActionsPanel, ItemLibraryDialog, SendDialog, ImportDocDialog,
  AssumptionsExclusionsSection,
} from "@/components/QuoteWorkspaceParts";
import QuoteProposalPreview from "@/components/proposal/QuoteProposalPreview";
import {
  MobileCustomerCard, MobileDetailsCard, MobileLineItems, MobilePricingCard,
  MobileMoreSection, MobileActionBar, MobileReviewScreen,
} from "@/components/QuoteMobileBuilder";
import { useQuoteEditor, isNewDraft, STATUS_STYLE, newItem } from "@/hooks/useQuoteEditor";

const ACCENTS = [
  { key: "orange", cls: "bg-[#FF5F15]" }, { key: "blue", cls: "bg-sky-500" },
  { key: "green", cls: "bg-emerald-500" }, { key: "purple", cls: "bg-violet-500" },
  { key: "grey", cls: "bg-zinc-500" },
];

export default function QuoteWorkspace() {
  const { qid } = useParams();
  const navigate = useNavigate();

  const {
    q, setQ, loading, saveState, clients, setClients, jobs,
    totals, patch, saveNow, items, setItems, updateItem, removeItem, duplicateItem, moveItem,
    addItem: addItemBase, addSection, dragRef, onDrop, sectionSubtotals, markSynced,
    openPdf, downloadPdf, uploadFiles, convertToInvoice, convertToJob, respond, saveAsTemplate,
  } = useQuoteEditor(qid);

  const [expandedRow, setExpandedRow] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(null); // "estimate" | "quote" | "duplicate"
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mobileReviewOpen, setMobileReviewOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const openProposalPreview = () => {
    if (isNewDraft(q)) { toast.error("Save the quote first to preview it"); return; }
    setPreviewOpen(true);
  };

  useEffect(() => {
    if (!fullScreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const h = (e) => { if (e.key === "Escape") setFullScreen(false); };
    window.addEventListener("keydown", h);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", h); };
  }, [fullScreen]);

  const addItem = (extra = {}) => { setExpandedRow(items.length); addItemBase(extra); };
  const removeItemAndCollapse = (i) => { removeItem(i); setExpandedRow(null); };

  if (loading || !q) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  }

  const validDays = q.valid_until && q.issue_date
    ? Math.round((new Date(q.valid_until) - new Date(q.issue_date)) / 86400000) : null;
  const status = q.status || "draft";
  const client = clients.find((c) => c.id === q.client_id);
  const editable = !["accepted", "declined", "converted"].includes(status);
  const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);

  const lineItemsSection = (
    <section className={fullScreen ? "flex flex-col h-full bg-white dark:bg-[#0F141C]" : "border-2 border-zinc-300 dark:border-white/15 bg-white dark:bg-[#0F141C]"} data-testid="quote-line-items">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03] shrink-0">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600 dark:text-zinc-300">
          <Layers className="w-3.5 h-3.5 text-[#FF5F15]" /> Line Items
          {items.filter((i) => !i.is_section).length > 0 && (
            <span className="font-mono text-zinc-400 dark:text-zinc-500 tracking-normal">({items.filter((i) => !i.is_section).length})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setLibraryOpen(true)} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="quote-add-from-library">
            <Library className="w-3 h-3 mr-1" /> Add from Library
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-7 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase tracking-wider" data-testid="quote-add-item-menu">
                <Plus className="w-3 h-3 mr-1" /> Add Item <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => addItem()} data-testid="add-line-item">Add Line Item</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "labour", unit: "hr" })}><Hammer className="w-3.5 h-3.5 mr-2" /> Add Labour</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "material" })}><Package className="w-3.5 h-3.5 mr-2" /> Add Material</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addItem({ category: "plant", unit: "hr" })}><Truck className="w-3.5 h-3.5 mr-2" /> Add Plant / Equipment</DropdownMenuItem>
              <DropdownMenuItem onClick={addSection} data-testid="add-section-menu"><Layers className="w-3.5 h-3.5 mr-2" /> Add Section</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setImportOpen("estimate")} data-testid="import-from-estimate">Import from Estimate</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="outline" onClick={() => setFullScreen((f) => !f)}
            className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15"
            title={fullScreen ? "Exit full screen (Esc)" : "Edit line items full screen"} data-testid="quote-fullscreen-toggle">
            {fullScreen ? <><Minimize2 className="w-3 h-3 mr-1" /> Exit full screen</> : <><Maximize2 className="w-3 h-3 mr-1" /> Full screen</>}
          </Button>
        </div>
      </div>
      <div className={fullScreen ? "flex-1 overflow-auto" : ""}>
        <LineItemsTable
          items={items} updateItem={updateItem} removeItem={removeItemAndCollapse} duplicateItem={duplicateItem}
          moveItem={moveItem} expandedRow={expandedRow} setExpandedRow={setExpandedRow}
          sectionSubtotals={sectionSubtotals} settings={q.settings} dragRef={dragRef} onDrop={onDrop} editable={editable}
        />
        <button onClick={addSection}
          className="m-3 w-[calc(100%-1.5rem)] border-2 border-dashed border-zinc-300 dark:border-white/15 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:border-[#FF5F15] hover:text-[#FF5F15] transition-colors"
          data-testid="quote-add-section">
          + Add Section
        </button>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen" data-testid="quote-workspace">
      <div className="px-4 lg:px-8 py-3 max-w-lg"><DocumentTemplateSelect kind="quote" value={q.template_id} disabled={status !== "draft"} onChange={(template_id, config) => patch({template_id, ...(!q.line_items?.length && config?.line_items?.length ? {line_items:config.line_items.map(item => ({...item}))} : {})})} /><p className="mt-1 text-xs text-muted-foreground">{status === "draft" ? "Applies to this document only. Job defaults are unchanged." : "Template locked after issue."}</p></div>
      {/* ===== sticky header ===== */}
      <div className="sticky top-0 z-30 border-b border-zinc-200 dark:border-white/10 bg-white/95 dark:bg-[#0B0F14]/95 backdrop-blur px-4 lg:px-8 py-3" data-testid="quote-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <button onClick={() => navigate("/app/quotes")} className="text-zinc-400 hover:text-[#FF5F15]" data-testid="quote-back">
                <ArrowLeft className="w-4.5 h-4.5" />
              </button>
              <h1 className="font-display font-black uppercase tracking-tight text-xl lg:text-2xl">
                {isNewDraft(q) ? "Create Quote" : q.quote_number}
              </h1>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.draft}`} data-testid="quote-status-badge">{status}</span>
              <span className="hidden md:inline text-[11px] text-zinc-400" data-testid="quote-autosave-state">
                {saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> All changes saved</span>
                  : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                  : "Unsaved changes"}
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5 ml-7 hidden sm:block">Create a professional quote and send it to your customer.</p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => saveNow()} className="h-8 rounded-sm uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15" data-testid="quote-save-draft">
              Save as Draft
            </Button>
            <Button variant="outline" size="sm" onClick={openProposalPreview} className="h-8 rounded-sm uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15" data-testid="quote-preview">
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px]" data-testid="quote-send-menu">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Send Quote <ChevronDown className="w-3 h-3 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem onClick={() => setSendOpen(true)} data-testid="quote-send-now"><Send className="w-3.5 h-3.5 mr-2" /> Send Quote</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await saveNow(); toast.success("Saved — send it whenever you're ready"); }} data-testid="quote-send-later"><CalendarClock className="w-3.5 h-3.5 mr-2" /> Save &amp; Send Later</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={downloadPdf} data-testid="quote-download-pdf"><FileText className="w-3.5 h-3.5 mr-2" /> Download PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openPdf(true)} data-testid="quote-print"><Printer className="w-3.5 h-3.5 mr-2" /> Print Quote</DropdownMenuItem>
                {status !== "converted" && (
                  <>
                    <DropdownMenuSeparator />
                    {status !== "accepted" && (
                      <DropdownMenuItem onClick={() => { if (window.confirm(`Mark ${q.quote_number} as accepted?`)) respond("accept"); }} data-testid="quote-mark-accepted">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Mark as Accepted
                      </DropdownMenuItem>
                    )}
                    {status !== "declined" && !q.converted_invoice_id && !q.converted_job_id && (
                      <DropdownMenuItem onClick={() => { setRejectReason(""); setRejectOpen(true); }} data-testid="quote-mark-rejected">
                        <XCircle className="w-3.5 h-3.5 mr-2 text-red-500" /> Mark as Rejected…
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {status === "accepted" && (
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="quote-accepted-actions">
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Accepted {q.accepted_at ? new Date(q.accepted_at).toLocaleDateString("en-NZ") : ""} — next:</span>
            {!q.converted_job_id && <Button size="sm" onClick={convertToJob} className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider" data-testid="quote-to-job"><Briefcase className="w-3 h-3 mr-1" /> Convert to Job</Button>}
            {q.converted_job_id && <Button size="sm" variant="outline" onClick={() => navigate(`/app/jobs/${q.converted_job_id}`)} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="quote-view-job"><Briefcase className="w-3 h-3 mr-1" /> View Job</Button>}
            {!q.converted_invoice_id && <Button size="sm" variant="outline" onClick={convertToInvoice} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="quote-to-invoice"><Receipt className="w-3 h-3 mr-1" /> Create Invoice</Button>}
            {q.converted_job_id && <Button size="sm" variant="outline" onClick={() => navigate("/app/scheduler")} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="quote-schedule"><CalendarClock className="w-3 h-3 mr-1" /> Schedule Work</Button>}
          </div>
        )}
        {status === "declined" && (
          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="quote-declined-banner">
            <span className="text-[10px] font-bold uppercase tracking-widest text-red-600">
              Rejected {q.declined_at ? new Date(q.declined_at).toLocaleDateString("en-NZ") : ""}
              {(q.declined_reason || q.acceptance?.comment) ? ` — ${q.declined_reason || q.acceptance?.comment}` : ""}
            </span>
            <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Mark ${q.quote_number} as accepted instead?`)) respond("accept"); }} className="h-7 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="quote-declined-mark-accepted">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark as Accepted
            </Button>
          </div>
        )}
      </div>

      {/* ===== two-column workspace (desktop / tablet) ===== */}
      <div className="hidden md:grid px-4 lg:px-8 py-5 lg:pb-10 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        {/* LEFT column */}
        <div className="space-y-4 min-w-0">
          <CustomerSection q={q} patch={patch} clients={clients} setClients={setClients} editable={editable} />
          <QuoteDetailsSection q={q} patch={patch} validDays={validDays} advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen} />

          {/* ===== LINE ITEMS — centrepiece ===== */}
          {fullScreen ? (
            <div className="border-2 border-dashed border-[#FF5F15]/40 bg-[#FF5F15]/5 px-4 py-6 text-center" data-testid="quote-line-items-placeholder">
              <p className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">Line items are open in full screen</p>
              <Button size="sm" variant="outline" onClick={() => setFullScreen(false)} className="mt-3 h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="quote-fullscreen-return">
                <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Return to page
              </Button>
            </div>
          ) : lineItemsSection}

          <LinkedJobSection q={q} patch={patch} jobs={jobs} />
          <AttachmentsSection q={q} setQ={setQ} uploadFiles={uploadFiles} />
          <NotesSection q={q} patch={patch} />
          <AssumptionsExclusionsSection q={q} patch={patch} />
          <TermsSection q={q} patch={patch} />
          <ActivitySection q={q} setQ={setQ} />
        </div>

        {/* RIGHT sidebar */}
        <div className="space-y-4 lg:sticky lg:top-[76px]">
          <SummaryPanel totals={totals} q={q} onPreview={openProposalPreview} onSend={() => setSendOpen(true)} />
          <DiscountPanel q={q} patch={patch} />
          <TaxPanel q={q} patch={patch} />
          <SettingsPanel q={q} patch={patch} />
          <QuickActionsPanel onEstimate={() => setImportOpen("estimate")} onDuplicate={() => setImportOpen("duplicate")} onTemplate={saveAsTemplate} onImportQuote={() => setImportOpen("quote")} />
        </div>
      </div>

      {/* ===== mobile-only quote builder (<768px) — same q/patch/items state & save logic ===== */}
      <div className="md:hidden px-3 py-3 pb-32 space-y-3" data-testid="quote-mobile-workspace">
        <MobileCustomerCard q={q} patch={patch} clients={clients} setClients={setClients} editable={editable} />
        <MobileDetailsCard q={q} patch={patch} validDays={validDays} />
        <MobileLineItems items={items} updateItem={updateItem} removeItem={removeItemAndCollapse} duplicateItem={duplicateItem}
          moveItem={moveItem} addItem={addItem} setItems={setItems} editable={editable} />
        <MobilePricingCard q={q} patch={patch} />
        <MobileMoreSection q={q} patch={patch} setQ={setQ} jobs={jobs} uploadFiles={uploadFiles}
          onEstimate={() => setImportOpen("estimate")} onDuplicate={() => setImportOpen("duplicate")}
          onTemplate={saveAsTemplate} onImportQuote={() => setImportOpen("quote")} />
      </div>
      <MobileActionBar q={q} totals={totals} saveState={saveState} onSaveDraft={() => saveNow()} onReview={() => setMobileReviewOpen(true)} />
      <MobileReviewScreen open={mobileReviewOpen} onClose={() => setMobileReviewOpen(false)} q={q} totals={totals} items={items}
        onPreviewPdf={() => openPdf(false)} onSend={() => { setMobileReviewOpen(false); setSendOpen(true); }} />

      {fullScreen && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-zinc-100 dark:bg-[#0B0F14]" data-testid="quote-fullscreen-overlay">
          <div className="shrink-0 flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]">
            <div className="flex items-center gap-3 min-w-0">
              <Layers className="w-4 h-4 text-[#FF5F15] shrink-0" />
              <span className="font-display font-black uppercase tracking-tight text-sm lg:text-base truncate">{isNewDraft(q) ? "Quote" : q.quote_number} — Line Items</span>
              <span className="hidden md:inline text-[11px] text-zinc-400">
                {saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Saved</span>
                  : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                  : "Unsaved changes"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total</span>
              <span className="font-mono font-black text-[#FF5F15] text-sm lg:text-base">{nzd(totals.total)}</span>
              <Button size="sm" onClick={() => setFullScreen(false)} className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="quote-fullscreen-done">
                <Minimize2 className="w-3.5 h-3.5 mr-1.5" /> Done
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-3 lg:p-5">
            <div className="h-full border-2 border-zinc-300 dark:border-white/15 flex flex-col overflow-hidden bg-white dark:bg-[#0F141C]">
              {lineItemsSection}
            </div>
          </div>
        </div>
      )}

      <QuoteProposalPreview open={previewOpen} onClose={() => setPreviewOpen(false)} quoteId={q.id} saveNow={saveNow} />

      <ItemLibraryDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} onAdd={(picked) => { setItems([...items, ...picked]); setLibraryOpen(false); }} />
      <SendDialog open={sendOpen} onClose={() => setSendOpen(false)} q={q} saveNow={saveNow} onSent={(data) => { markSynced(); setQ((prev) => ({ ...prev, ...data })); }} />
      <ImportDocDialog mode={importOpen} onClose={() => setImportOpen(null)} currentId={q.id}
        onPick={async (doc) => {
          if (importOpen === "duplicate") {
            try { const { data } = await api.post(`/quotes/${doc.id}/duplicate`); toast.success(`Duplicated as ${data.quote_number}`); navigate(`/app/quotes/${data.id}`); }
            catch { toast.error("Duplicate failed"); }
          } else {
            setItems([...items, ...((doc.line_items || []).map((li) => ({ ...newItem(), ...li })))]);
            toast.success(`${(doc.line_items || []).length} line(s) imported from ${doc.quote_number}`);
          }
          setImportOpen(null);
        }} />

      <Dialog open={rejectOpen} onOpenChange={(o) => { if (!o) setRejectOpen(false); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="quote-ws-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight">Reject quote {q.quote_number}</DialogTitle>
            <DialogDescription>Give a reason so the team knows why this one didn&apos;t land.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Went with another contractor — our price was too high"
            rows={3}
            className="rounded-sm"
            data-testid="quote-ws-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="quote-ws-reject-cancel">Cancel</Button>
            <Button
              onClick={async () => {
                if (!rejectReason.trim()) { toast.error("Please give a reason for rejecting this quote"); return; }
                setRejectBusy(true);
                const ok = await respond("decline", rejectReason.trim());
                setRejectBusy(false);
                if (ok) setRejectOpen(false);
              }}
              disabled={rejectBusy || !rejectReason.trim()}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white uppercase tracking-wider font-bold text-xs"
              data-testid="quote-ws-reject-confirm">
              {rejectBusy ? "Rejecting…" : "Reject quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
